import type { MouseEvent } from "react";
import { FilterIndicator, SortIndicator, sortableHeaderClass, type SortDirection } from "@/components/base/column-filter-popover";

export interface ColumnHeaderProps {
    label: string;
    isFilterable?: boolean;
    isFilterActive?: boolean;
    isFilterOpen?: boolean;
    onFilterPress?: (anchorEl: HTMLElement) => void;
    isSortable?: boolean;
    showSortControl?: boolean;
    isSortActive?: boolean;
    sortDirection?: SortDirection;
    onSort?: () => void;
    align?: "left" | "right";
    className?: string;
}

export function ColumnHeader({
    label,
    isFilterable = false,
    isFilterActive = false,
    isFilterOpen = false,
    onFilterPress,
    isSortable = false,
    showSortControl = isSortable,
    isSortActive = false,
    sortDirection,
    onSort,
    align = "left",
    className = "px-4 py-3",
}: ColumnHeaderProps) {
    const isRightAligned = align === "right";
    const isFilterHighlighted = isFilterActive || isFilterOpen;
    const handleFilterPress = (event: MouseEvent<HTMLButtonElement>) => onFilterPress?.(event.currentTarget);

    return (
        <th className={`${className}${isRightAligned ? " text-right" : ""}`}>
            <div className={`relative flex items-center gap-1${isRightAligned ? " justify-end" : ""}`}>
                {isFilterable ? (
                    <button
                        type="button"
                        className={`-ml-1 inline-flex cursor-default items-center gap-1 rounded px-1 py-0.5 text-xs font-medium ${isRightAligned ? "text-right" : "text-left"} ${
                            isFilterHighlighted ? "bg-[rgba(5,120,255,0.08)] text-blue-600" : "text-gray-500 hover:bg-[rgba(0,0,0,0.04)]"
                        }`}
                        onClick={handleFilterPress}
                        aria-label={`Filter by ${label}`}
                    >
                        {label}
                        <FilterIndicator />
                    </button>
                ) : (
                    <span className="cursor-default text-xs font-medium text-gray-500">{label}</span>
                )}
                {showSortControl && (
                    <button
                        type="button"
                        disabled={!isSortable}
                        className={`${sortableHeaderClass(isSortActive)} cursor-default${isSortable ? "" : " opacity-50"}`}
                        onClick={isSortable ? onSort : undefined}
                        aria-label={isSortable ? `Sort by ${label}` : `Sort by ${label} (unavailable)`}
                        title={isSortable ? undefined : "Sorting isn't available yet"}
                    >
                        <SortIndicator direction={isSortActive ? sortDirection : undefined} />
                    </button>
                )}
            </div>
        </th>
    );
}
